<template>
  <transition name="fade-from-bottom">
    <div
      ref="modal"
      class="purchase-modal"
      :class="{ show, 'full-page': fullPage }"
      :style="show ? `bottom: -${Math.max(reducer.diffY, 0)}px;` : ''"
    >
      <button
        ref="reducer"
        class="purchase-modal__reducer"
        @pointerdown="handleReducerPointerDown"
      >
        <span class="d-flex justify-center align-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="122"
            height="12"
            viewBox="0 0 122 12"
            fill="none"
          >
            <rect x="31" y="5" width="60" height="2" rx="1" fill="#D9D9D9" />
          </svg>
        </span>
      </button>

      <div
        class="purchase-modal__nav d-flex justify-space-between align-center pb-0-6"
      >
        <link-button
          with-icon
          icon-position="left"
          icon="carret-left"
          class="purchase-modal__back-button"
          @click="handleHideModal"
        >
          <span class="text-label text-uppercase w-600">{{ title }}</span>
        </link-button>

        <slot name="top-label" />
      </div>

      <slot name="error" />

      <slot name="top" />

      <div class="purchase-modal__content">
        <slot name="content" />
      </div>

      <slot name="bottom" />
    </div>
  </transition>
</template>

<script>
import { mapMutations } from 'vuex'

import LinkButton from '@/components/buttons/Link'

export default {
  name: 'PurchaseModal',
  components: {
    LinkButton,
  },
  props: {
    title: {
      type: String,
      default: 'Purchase',
    },
    show: {
      type: Boolean,
      default: false,
    },
    fullPage: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    reducer: {
      startY: 0,
      diffY: 0,
      hasMoved: false,
    },
  }),
  watch: {
    show(val) {
      if (this.fullPage) {
        if (val) {
          this.setScroll(true)
        } else {
          this.setScroll(false)
        }
      }
    },
  },
  methods: {
    ...mapMutations({
      setScroll: 'behavior/SET_SCROLL',
    }),
    handleHideModal() {
      this.$emit('hide')
    },
    /**
     * Reducer
     */
    handleReducerPointerDown(ev) {
      ev.preventDefault()
      ev.stopPropagation()

      const target = this.$refs.modal
      target.classList.add('no-animate')
      target.classList.add('grabbing')

      this.reducer.startY = ev.clientY
      this.reducer.hasMoved = false

      target.setPointerCapture(ev.pointerId)

      const pointerMove = (ev) => {
        this.reducer.diffY = ev.clientY - this.reducer.startY

        if (this.reducer.diffY > 5) {
          ev.preventDefault()
          this.reducer.hasMoved = true
        } else {
          this.reducer.hasMoved = false
        }
      }

      const pointerUp = (ev) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (this.reducer.hasMoved) {
          this.$emit('hide')
        }

        this.reducer = {
          startY: 0,
          diffY: 0,
          hasMoved: false,
        }

        target.classList.remove('no-animate')
        target.classList.remove('grabbing')
        target.releasePointerCapture(ev.pointerId)

        window.removeEventListener('pointermove', pointerMove)
        window.removeEventListener('pointerup', pointerUp)
      }

      window.addEventListener('pointermove', pointerMove)
      window.addEventListener('pointerup', pointerUp)
    },
  },
}
</script>

<style lang="scss">
.purchase-modal {
  touch-action: none;
  position: fixed;
  display: flex;
  flex-direction: column;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 0 calc(env(safe-area-inset-bottom) + var(--spacing) * 1.4);
  background-color: white;
  z-index: 3000;
  transition: all 0.3s var(--asphalte-animation-function);
  transform: translateY(100%);
  opacity: 0;

  &.show {
    transform: translateY(0);
    opacity: 1;
  }

  &__reducer {
    position: relative;
    display: block;
    border: 0;
    background-color: transparent;
    padding: 0;
    width: 100%;

    svg {
      rect {
        transition: all 0.3s var(--asphalte-animation-function);
      }
    }

    &::before {
      content: '';
      position: absolute;
      inset: rem(-10px);
      background-color: transparent;
    }
  }

  &__back-button {
    padding: calc(var(--spacing) * 0.8) calc(var(--spacing));
  }

  &__content {
    box-shadow: 0 2px 7px 0 RGB(0 0 0 / 0.1) inset;
  }

  &.grabbing {
    .purchase-modal__reducer {
      svg {
        rect {
          x: 0;
          width: 100%;
          height: 2px;
        }
      }
    }
  }

  &.full-page {
    top: 0;

    .purchase-modal__content {
      flex: 1 1 auto;
      padding: var(--spacing) 0;
      overflow: auto;
    }
  }
}
</style>
